import dayjs from 'dayjs'
import { useCallback, useMemo, useReducer, useState } from 'react'
import Pet from '~models/pet'
import { validatePetName } from '~utils/validations'

type Props = Readonly<Pet.Level1>

export type Age = {
  label: string
  value: number
}

type Action =
  | {
      type: 'SET_NAME'
      payload: string
    }
  | {
      type: 'SET_GENDER'
      payload: Pet.Gender
    }
  | {
      type: 'SET_IS_NEUTERED'
      payload: boolean
    }
  | {
      type: 'SET_SPECIES'
      payload: string
    }
  | {
      type: 'SET_BREED'
      payload: string
    }
  | {
      type: 'SET_AGE'
      payload: string
    }
  | {
      type: 'SET_PHOTO_URL'
      payload: string
    }
  | {
      type: 'RESET'
    }

/**
 *
 * @param value ex) 02212021
 * @returns ex) '02/21/2021'
 */
export const formatPetAge = (value: string) => {
  let result = value.replace(/\D/g, '') // 숫자가 아닌 모든 문자 제거

  if (result.length > 2) {
    result = result.slice(0, 2) + '/' + result.slice(2)
  }
  if (result.length > 5) {
    result = result.slice(0, 5) + '/' + result.slice(5)
  }
  if (result.length > 10) {
    result = result.slice(0, 10) // 최대 10자리까지만 허용
  }

  return result
}

const isValidDate = (
  dateString: string,
  onError?: (error: string) => void,
  // 정규식을 사용하여 mm/dd/yyyy 형식 확인
  pattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/
) => {
  if (!pattern.test(dateString)) {
    onError?.(`Please enter the correct date format (MM/DD/YYYY).`)
    return false
  }

  // 날짜 파싱
  const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10))
  const date = new Date(year, month - 1, day)

  // 유효한 날짜인지 확인
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    onError?.(`Please enter your pet's birthdate (or best estimate) in MM/DD/YYYY format.`)
    return false
  }

  if (year <= 1900 && month <= 1 && day <= 1) {
    onError?.(`Please enter your pet's birthdate (or best estimate) in MM/DD/YYYY format.`)
    return false
  }

  // 현재 날짜와 비교하여 미래 날짜인지 확인
  const today = new Date()
  today.setHours(0, 0, 0, 0) // 시간을 0으로 설정하여 날짜만 비교

  if (date > today) {
    onError?.(`Please enter a valid birthdate.`)
    return false
  }

  return true
}

const validateAge = (value: string) => {
  let isAgeValidating = true
  let ageErrorMessage = ''

  if (!value.length) {
    ageErrorMessage = ''
    isAgeValidating = false
    return {
      isAgeValidating,
      ageErrorMessage,
    }
  }

  // value가 8자리 이하일땐 error message를 띄우지 않음
  if (value.length <= 8) {
    isAgeValidating = true
    return {
      isAgeValidating,
      ageErrorMessage,
    }
  }

  if (
    isValidDate(value, error => {
      ageErrorMessage = error
    })
  ) {
    ageErrorMessage = ''
    isAgeValidating = false
  }

  return {
    isAgeValidating,
    ageErrorMessage,
  }
}

const useFormattedPetAge = (date: string) => {
  const [petAgeDate, setPetAgeDate] = useState(date)
  const formattedAgeDate = formatPetAge(petAgeDate)
  const { isAgeValidating, ageErrorMessage } = validateAge(formattedAgeDate)

  return {
    setPetAgeDate,
    formattedAgeDate,
    isAgeValidating,
    ageErrorMessage,
  }
}

const usePetInfo = (initialPetData?: Props) => {
  const petData = useMemo(
    () =>
      ({
        name: initialPetData ? initialPetData.name : '',
        gender: initialPetData ? initialPetData.gender : null,
        species: initialPetData ? initialPetData.kind.depth1 : null,
        breed: initialPetData ? initialPetData.kind.depth2 : null,
        age: initialPetData ? dayjs(initialPetData.born_at).format('MM/DD/YYYY') : '',
        is_neutered: initialPetData ? initialPetData.is_neutered : null,
        photo_url: initialPetData ? initialPetData.photo_url : 'null',
      }) satisfies Pet.UpdateDraft,
    [initialPetData]
  )

  const { formattedAgeDate, isAgeValidating, ageErrorMessage, setPetAgeDate } = useFormattedPetAge(
    petData.age
  )

  const reducer = useCallback(
    (state: Pet.UpdateDraft, action: Action) => {
      switch (action.type) {
        case 'SET_NAME':
          return { ...state, name: action.payload }
        case 'SET_GENDER':
          return { ...state, gender: action.payload }
        case 'SET_IS_NEUTERED':
          return { ...state, is_neutered: action.payload }
        case 'SET_SPECIES':
          return { ...state, species: action.payload, breed: null }
        case 'SET_BREED':
          return { ...state, breed: action.payload }
        case 'SET_PHOTO_URL':
          return { ...state, photo_url: action.payload }
        case 'SET_AGE': {
          const value = action.payload.replace(/[^0-9]/g, '')

          if (value.length > 10) {
            return state
          }
          setPetAgeDate(value)

          return {
            ...state,
            age: action.payload,
          }
        }
        case 'RESET':
          setPetAgeDate('')

          return {
            name: '',
            gender: null,
            is_neutered: null,
            species: null,
            breed: null,
            photo_url: 'null',
            age: '',
          } satisfies Pet.UpdateDraft
        default:
          return state
      }
    },
    [setPetAgeDate]
  )

  const [pet, setPet] = useReducer(reducer, petData)

  const nameErrorMessage = pet.name?.length ? validatePetName(pet.name) : ''

  return {
    pet,
    nameErrorMessage,
    formattedAgeDate,
    isAgeValidating,
    ageErrorMessage,
    setPet,
  }
}

export default usePetInfo
